import { ESM_ORIGIN, unpkgFileUrl } from '../moduleOrigins';
import { ICDNModule } from './module-cdn';
import { retryFetch } from '../../utils/fetch';

// esm.sh fallback for packages the primary dependency CDN (blazingly.io) cannot
// resolve — most often a version newer than its npm mirror has ingested. esm.sh
// tracks npm in near real time, so it covers the gap.
//
// Approach (transpile-through, shared React): we fetch the package's esm.sh
// SOURCE with the already-resolved deps marked `?external=...`, so esm.sh emits
// bare `import ... from "react"` (etc.) instead of bundling its own copies. That
// source is registered as a synthetic package and transpiled by the SAME chain
// the bundler runs, so its `require("react")` resolves through the bundler to the
// APP's React instance. This is what makes a React component library work: an
// earlier native-`import()` approach gave the fallback its OWN esm.sh React, and
// any package calling a hook against it (lucide-react@1.x reads `IconContext` via
// `useContext`) crashed with a null dispatcher. Sharing React via the bundler's
// own resolution avoids that entirely — no import map, no global, no dual React.
//
// Scope: handles a package whose esm.sh output is a single self-contained module
// (after following esm.sh's re-export stub) importing only the externalized,
// already-resolved deps — the common "newer version of a leaf library" case.
// A multi-internal-chunk package throws the clear "could not resolve" error
// rather than silently shipping a broken module.
//
// CSP: the BASELINE sandbox document (index.html) ships no CSP, and this is a
// direct cross-origin fetch from the iframe (not parent-forwarded); esm.sh sends
// `access-control-allow-origin: *`. The M3 document (m3.html, §G1a / R3-234) DOES
// carry a CSP, and it lists this origin in `connect-src` — via
// `bundler/moduleOrigins.ts`, which is why ESM_ORIGIN is imported rather than
// declared here.

export type EsmFallbackFetcher = (url: string) => Promise<string>;

/** Default source fetcher — a plain cross-origin `fetch` (CORS-allowed by esm.sh). */
export const nativeEsmFallbackFetcher: EsmFallbackFetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`);
  return res.text();
};

/** Build the esm.sh entry URL. Every already-resolved dependency is externalized
 *  so esm.sh emits bare imports for them — the bundler then resolves each to the
 *  shared instance it already has, instead of esm.sh bundling a duplicate. */
export function esmFallbackEntryUrl(name: string, range: string, externals: readonly string[]): string {
  const ext = externals.length ? `external=${[...externals].sort().join(',')}&` : '';
  return `${ESM_ORIGIN}/${name}@${range}?${ext}target=es2022`;
}

/** A specifier that points back into esm.sh (an internal chunk), vs a bare
 *  package name (`react`) that the bundler resolves. */
export function isEsmInternal(spec: string): boolean {
  return spec.startsWith('/') || spec.startsWith('./') || spec.startsWith('../') || spec.startsWith(ESM_ORIGIN);
}

/** Every module specifier the source imports/re-exports (static + dynamic). */
export function importedSpecifiers(src: string): string[] {
  const specs: string[] = [];
  const fromRe = /\bfrom\s*["']([^"']+)["']/g; // import ... from / export ... from
  const dynRe = /\bimport\s*\(\s*["']([^"']+)["']\s*\)/g;
  for (const re of [fromRe, dynRe]) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(src)) !== null) specs.push(m[1]);
  }
  return specs;
}

/** If `src` is a pure esm.sh re-export stub (only `export * / {…} from "<one
 *  internal url>"`, no other code), return that internal URL; else null. esm.sh's
 *  package entry is such a stub pointing at the real `.mjs`. */
export function parseReexportStub(src: string): string | null {
  const stripped = src
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/^\s*\/\/[^\n]*$/gm, '')
    .trim();
  if (!stripped) return null;
  const targets = new Set<string>();
  for (const stmt of stripped
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)) {
    const m = stmt.match(/^export\s+(?:\*(?:\s+as\s+\w+)?|\{[^}]*\})\s+from\s*["']([^"']+)["']$/);
    if (!m) return null; // any non-reexport statement → not a stub
    targets.add(m[1]);
  }
  if (targets.size !== 1) return null;
  const url = [...targets][0];
  return isEsmInternal(url) ? new URL(url, ESM_ORIGIN).href : null;
}

/**
 * Fetch the package's esm.sh source, following re-export stubs to the real
 * module. Returns a single source whose only imports are bare (externalized)
 * specifiers. Throws if it still references esm.sh-internal chunks (multi-module
 * package — out of scope for the single-module fallback).
 */
export async function fetchEsmFallbackSource(
  entryUrl: string,
  fetcher: EsmFallbackFetcher,
  maxHops = 8,
): Promise<string> {
  let url = entryUrl;
  let src = await fetcher(url);
  for (let hop = 0; hop < maxHops; hop++) {
    const next = parseReexportStub(src);
    if (!next) break;
    url = next;
    src = await fetcher(url);
  }
  const internal = importedSpecifiers(src).filter(isEsmInternal);
  if (internal.length) {
    throw new Error(
      `esm.sh module spans ${internal.length} internal chunk(s) (e.g. "${internal[0]}"); ` +
        `the single-module fallback can't inline it`,
    );
  }
  return src;
}

/** Path the synthetic fallback package is mounted at (its transpile path also
 *  selects the PLAIN chain — under /node_modules, so no react-refresh wrap). */
export const esmFallbackModulePath = (name: string): string => `/node_modules/${name}/index.js`;

/** Path a materialized fallback SUBPATH lands at — the one spelling shared by
 *  the registry's materialization write and the bundler's CachedFS negative
 *  drop, whose byte-for-byte agreement the retry depends on (R3-820 round 1). */
export const esmFallbackSubpathPath = (name: string, subpath: string): string => `/node_modules/${name}/${subpath}`;

/**
 * Shape the transpiled fallback as an `ICDNModule` so it flows through the normal
 * NodeModule → RegistryFS → resolution path. `code` is already CJS (transpiled by
 * the shared chain); `deps` are its bare `require()` specifiers (e.g. `react`),
 * resolved by the bundler to the shared instances. `t: true` keeps it from being
 * re-transpiled.
 */
export function synthesizeEsmFallbackModule(
  name: string,
  range: string,
  code: string,
  deps: readonly string[],
): ICDNModule {
  return {
    f: {
      'package.json': { c: JSON.stringify({ name, version: range, main: 'index.js' }), d: [], t: true },
      'index.js': { c: code, d: [...deps], t: true },
    },
    m: [],
  };
}

/**
 * Split a BARE specifier with a subpath (`name/sub`, `@scope/name/sub`) into its
 * package name and the package-relative subpath. Null for everything else: a
 * bare package ROOT (`name`, `@scope/name` — no subpath to materialize),
 * relative (`./x`, `../y`), absolute (`/x`), `node:` builtins, and URL schemes
 * (`https:`, `data:`). The esm.sh fallback synthesizes ONLY the package root
 * (`synthesizeEsmFallbackModule`), so a bare subpath of a fallback package is
 * the one resolution shape whose target can still be missing — this predicate
 * names exactly that case for the resolution-retry hook.
 */
export function parseBareSubpath(specifier: string): { name: string; subpath: string } | null {
  if (!specifier || specifier.startsWith('.') || specifier.startsWith('/') || specifier.startsWith('node:')) {
    return null;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(specifier)) return null; // URL scheme (https:, data:, …)
  const segs = specifier.split('/');
  let name: string;
  let rest: string[];
  if (segs[0].startsWith('@')) {
    if (segs.length < 3 || !segs[1]) return null; // '@scope' or the package root — no subpath
    name = `${segs[0]}/${segs[1]}`;
    rest = segs.slice(2);
  } else {
    if (segs.length < 2 || !segs[0]) return null; // the package root
    name = segs[0];
    rest = segs.slice(1);
  }
  const subpath = rest.join('/');
  if (!subpath) return null;
  return { name, subpath };
}

/**
 * Raw-tarball file loader for subpaths of an esm.sh-fallback package. esm.sh
 * serves a package's SUBPATH as a re-export stub whose real chunk imports
 * esm.sh-internal `/node/*.mjs` shims — a multi-chunk shape the single-module
 * fallback refuses by design. The npm tarball carries the same file RAW
 * (`sql.js/dist/sql-asm.js` is the published Emscripten CJS itself), so the
 * subpath rail fetches from unpkg exactly the bytes the primary CDN would have
 * served had it carried the version.
 */
export type EsmFallbackSubpathLoader = (name: string, range: string, subpath: string) => Promise<string>;

// Range → exact version, memoized for this worker's lifetime — a deliberate
// pin: every subpath file fetch of the frame reuses it, so unpkg's range
// redirect can never skew two files of one package onto different releases
// mid-boot. The PROBE itself must stay out of the persistent immutable cache
// (`bypassImmutableCache`): a `name@^range/package.json` URL does not encode
// the exact version, and caching it would freeze the floating resolution
// across sessions. A failed resolution is not a fact — the memo entry is
// dropped so the next materialization re-probes.
const exactVersions = new Map<string, Promise<string>>();

async function resolveExactVersion(name: string, range: string): Promise<string> {
  const key = `${name}@${range}`;
  let promise = exactVersions.get(key);
  if (!promise) {
    promise = (async () => {
      const response = await retryFetch(`${unpkgFileUrl(name, range, 'package.json')}`, {
        maxRetries: 5,
        bypassImmutableCache: true,
      });
      // No ok check needed here: bypassing the immutable cache routes through
      // retryFetchUncached, which throws a FetchError on any non-ok response.
      const pkg = JSON.parse(await response.text()) as { version?: string };
      if (!pkg.version) throw new Error(`unpkg package.json for ${name}@${range} carries no version`);
      return pkg.version;
    })();
    exactVersions.set(key, promise);
    promise.catch(() => exactVersions.delete(key));
  }
  return promise;
}

export const nativeEsmFallbackSubpathLoader: EsmFallbackSubpathLoader = async (name, range, subpath) => {
  const version = await resolveExactVersion(name, range);
  // Exact-version URLs are immutable — this fetch MAY ride the persistent cache.
  const response = await retryFetch(unpkgFileUrl(name, version, subpath), { maxRetries: 5 });
  // Not strictly dead: the parent-forwarded immutable bridge can hand back a
  // non-ok Response with the status passed through (utils/fetch.ts).
  if (!response.ok) throw new Error(`HTTP ${response.status} fetching unpkg ${name}@${version}/${subpath}`);
  return response.text();
};
